<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { computed, useSlots } from "vue";
import { ActiveRouteStatuses } from "@/constants/trip";
import RouteStatusChip from "@/components/chips/RouteStatusChip.vue";
import RouteStopTime from "@/components/times/RouteStopTime.vue";
import type { TruckRoute } from "@/api/trucks/dto/truck.dto";

const { t } = useI18n();
const slots = useSlots();

const props = withDefaults(
  defineProps<{
    route: TruckRoute;
    index: number;
    tripId: number;
    currentRouteId?: number;
  }>(),
  {
    currentRouteId: undefined,
  },
);

const showYouViewRoute = computed(() => {
  return props.route.id === props.currentRouteId;
});

const classes = computed(() => {
  const result = [];

  if (ActiveRouteStatuses.includes(props.route.status)) {
    result.push("bg-green-lighten-5");
  } else {
    result.push("bg-grey-lighten-4");
  }

  if (showYouViewRoute.value) {
    result.push("border-primary border-width-2");
  }

  return result;
});

const firstStop = computed(() => {
  return props.route.routeStops[0];
});

const lastStop = computed(() => {
  return props.route.routeStops[props.route.routeStops.length - 1];
});

const facilityWay = computed(() => {
  return `${firstStop.value.facility?.address} -> ${lastStop.value.facility?.address}`;
});
</script>

<template>
  <div :class="classes" class="pa-4 rounded">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <span class="text-primary">{{ t("general.route") }} #{{ route.id }}</span>
        <route-status-chip :status="route.status" class="ml-3" />
        <div v-if="showYouViewRoute" class="d-flex align-center ml-5">
          <v-icon color="grey-darken-1" size="18">mdi-eye-outline</v-icon>
          <span class="ml-1 text-grey-darken-1 font-size-12">{{ t("trip.you-view-this-route") }}</span>
        </div>
      </div>

      <slot v-if="slots.actions" name="actions" v-bind="route"></slot>

      <v-btn
        v-else
        :to="{ name: 'trip-view', params: { id: props.route.tripId } }"
        class="font-size-11 mr-6 font-weight-600 text-uppercase"
        color="success"
        size="small"
        target="_blank"
        variant="flat"
      >
        View trip
      </v-btn>
    </div>

    <div class="mt-3">{{ facilityWay }}</div>

    <route-stop-time
      :stop-timezone="firstStop.timezone"
      :time-to="firstStop.timeTo"
      :time-type="firstStop.timeType"
      class="mt-2"
    />
  </div>
</template>

<style lang="scss" scoped></style>
